import {
  EOptionType,
  IAnswerSurveyDynamic,
  IQuestionSurveyDynamic,
  ISurveyDynamic,
} from 'app/api/dental-treatment/survey-dynamic/type'
import { BaseCheckbox } from 'app/components/common/BaseCheckbox'
import { BaseCol } from 'app/components/common/BaseCol'
import { BaseDivider } from 'app/components/common/BaseDivider'
import { BaseRow } from 'app/components/common/BaseRow'
import { BaseSpace } from 'app/components/common/BaseSpace'
import BaseText, { FontWeightType } from 'app/components/common/BaseText'
import { Loading } from 'app/components/common/Loading'
import { isEmpty, isEqual, isNil } from 'lodash'
import { BORDER_RADIUS, normalizePhone, PADDING } from 'parkway-web-common'
import { ReactNode } from 'react'
import styled from 'styled-components'
import { useHook } from './hook'
import { BaseImage } from 'app/components/common/BaseImage'

const InfoWrapper = ({
  children,
  title,
}: {
  title?: string
  children: ReactNode
}) => {
  return (
    <BaseSpace className="info">
      <BaseText children={title} fontWeight="medium" />
      <BaseSpace>{children}</BaseSpace>
    </BaseSpace>
  )
}

const ItemInfo = ({
  label,
  value,
  fontWeight,
}: {
  label?: string
  value?: string
  fontWeight?: FontWeightType
}) => {
  return (
    <BaseRow gutter={[16, 16]} align={'middle'}>
      <BaseCol xl={4}>
        <BaseText children={label} style={{ color: '#858C94' }} />
      </BaseCol>
      <BaseCol xl={20}>
        <BaseText
          children={!isEmpty(value) ? value : '-'}
          fontWeight={fontWeight}
        />
      </BaseCol>
    </BaseRow>
  )
}

const ItemRadio = ({
  question,
  answers,
}: {
  question: IQuestionSurveyDynamic
  answers?: IAnswerSurveyDynamic[]
}) => {
  const itemExist = answers?.find(item => isEqual(item?.id, question?.id))

  return (
    <BaseRow align={'middle'} justify={'space-between'} gutter={[16, 16]}>
      <BaseCol xl={10}>
        <BaseText children={question?.name} fontSize="xxs" />
      </BaseCol>
      <BaseCol xl={14}>
        <BaseRow align={'middle'} gutter={[16, 16]} wrap={false}>
          {question?.options?.map((option, idxOption) => {
            return (
              <BaseCol key={idxOption} xl={12}>
                <BaseCheckbox
                  disabled
                  className="item-checkbox-area"
                  checked={isEqual(itemExist?.value, option?.id)}
                >
                  <BaseText
                    children={option?.name}
                    style={{ color: '#6D7580' }}
                  />
                </BaseCheckbox>
              </BaseCol>
            )
          })}
        </BaseRow>
      </BaseCol>
    </BaseRow>
  )
}

const ItemRadioInput = ({
  question,
  isMain = false,
  answers,
}: {
  question: IQuestionSurveyDynamic
  isMain?: boolean
  answers?: IAnswerSurveyDynamic[]
}) => {
  const itemExist = answers?.find(item => isEqual(item?.id, question?.id))

  const listShowNote = question?.options?.filter(itm => itm?.showNote)

  const itemCheckedHaveShowNote = !!listShowNote?.find(option =>
    isEqual(itemExist?.value, option?.id),
  )

  return (
    <BaseSpace>
      <BaseText
        children={
          isMain ? `${question?.index}. ${question?.name}` : question?.name
        }
        fontSize={isMain ? 'xs' : 'xxs'}
        fontWeight={isMain ? 'medium' : 'regular'}
      />
      <BaseRow align={'middle'} gutter={[16, 16]} wrap={false}>
        {question?.options?.map((option, idxOption) => {
          return (
            <BaseCol key={idxOption} xl={12}>
              <BaseCheckbox
                disabled
                className="item-checkbox-area"
                checked={isEqual(itemExist?.value, option?.id)}
              >
                <BaseText
                  children={option?.name}
                  style={{ color: '#6D7580' }}
                />
              </BaseCheckbox>
            </BaseCol>
          )
        })}
      </BaseRow>

      {itemCheckedHaveShowNote ? (
        <div className="item-checkbox-area">
          <BaseText
            children={itemExist?.note ?? ''}
            style={{ color: '#6D7580' }}
          />
        </div>
      ) : null}
    </BaseSpace>
  )
}

const ItemQuestion = ({
  question,
  isMain,
  answers,
}: {
  question: IQuestionSurveyDynamic
  isMain?: boolean
  answers?: IAnswerSurveyDynamic[]
}) => {
  switch (question?.type) {
    case EOptionType.RADIO:
      return <ItemRadio question={question} answers={answers} />
    case EOptionType.CHECKBOX:
    case EOptionType.TEXT_INPUT:
      return <div />
    case EOptionType.RADIO_INPUT:
      return (
        <ItemRadioInput question={question} isMain={isMain} answers={answers} />
      )
    default:
      return <div />
  }
}

export const MedicalHistoryLayout = ({ item }: { item: ISurveyDynamic }) => {
  const { dataQuestion, isLoadingQuestion } = useHook()

  return (
    <BaseSpace>
      <ContentWrapper>
        <InfoWrapper title="Thông tin cá nhân">
          <ItemInfo
            label="Họ và tên"
            value={item?.patient?.name}
            fontWeight="semibold"
          />
          <ItemInfo
            label="Số điện thoại"
            value={normalizePhone(item?.patient?.contact ?? '') ?? ''}
          />
        </InfoWrapper>

        <BaseSpace className="info question">
          {dataQuestion?.map((question, indexQuestion) => {
            if (!isNil(question?.items)) {
              return (
                <BaseSpace key={indexQuestion}>
                  <BaseText
                    children={`${question?.index}. ${question?.name}`}
                    fontWeight="medium"
                    fontSize="xs"
                  />
                  <BaseSpace split={<BaseDivider className="divider" />}>
                    {question?.items?.map((itm, idx) => {
                      return (
                        <ItemQuestion
                          answers={item?.answers}
                          question={itm}
                          key={idx}
                        />
                      )
                    })}
                  </BaseSpace>
                </BaseSpace>
              )
            }

            return (
              <ItemQuestion
                isMain
                question={question}
                key={indexQuestion}
                answers={item?.answers}
              />
            )
          })}

          {!isEmpty(item?.signatureUrl) ? (
            <>
              <BaseDivider className="divider" type="horizontal" />

              <BaseRow align={'middle'} justify={'end'} gutter={16}>
                <BaseCol>
                  <BaseSpace>
                    <BaseText
                      children={'Khách hàng/ người dám hộ'}
                      fontWeight="medium"
                      textAlign="center"
                    />
                    <BaseImage
                      src={item?.signatureUrl}
                      preview={false}
                      width={150}
                      height={100}
                    />

                    <BaseText
                      children={item?.patient?.name}
                      fontWeight="semibold"
                      textAlign="center"
                    />
                  </BaseSpace>
                </BaseCol>
              </BaseRow>
            </>
          ) : null}
        </BaseSpace>
      </ContentWrapper>

      {isLoadingQuestion ? <Loading isAbsolute size="small" /> : null}
    </BaseSpace>
  )
}

const ContentWrapper = styled(BaseSpace)`
  background-color: #fcfcfc;
  border-radius: ${BORDER_RADIUS};
  padding: ${PADDING.xxs};
  border-color: 1px solid #f4f6f9;

  .info {
    background-color: white;
    padding: ${PADDING.md};
    border-radius: ${BORDER_RADIUS};
  }

  .divider {
    margin-top: 0px;
    margin-bottom: 0px;
  }

  .ant-checkbox-disabled.ant-checkbox-checked .ant-checkbox-inner {
    border-color: #a5abb3 !important;
    background-color: #a5abb3 !important;
  }

  .ant-checkbox-disabled.ant-checkbox-checked .ant-checkbox-inner::after {
    border-color: white;
  }

  .ant-checkbox-disabled .ant-checkbox-inner {
    border-color: #ebeef2;
    background-color: #ebeef2;
  }

  .item-checkbox-area {
    padding: 8px 8px;
    border-radius: 8px;
    border: 1px solid #ebeef2;
    width: 100%;
  }

  .question {
    height: 500px;
    overflow-y: auto;
  }
`
